import { NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AddressInputsComponent } from '@components/address-inputs/address-inputs.component';
import { UpsellCardComponent } from '@components/upsell-card/upsell-card.component';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { SelectableOption } from '@models/selectable-option';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { FormService } from '@services/form.service';

@Component({
  selector: 'app-medication-pickup-delivery',
  templateUrl: './medication-pickup-delivery.component.html',
  styleUrls: ['./medication-pickup-delivery.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [UpsellCardComponent, NgIf, AddressInputsComponent],
})
export class MedicationPickupDeliveryComponent implements AfterViewInit {
  @ViewChild('upsellCard', { static: false }) public upsellCard: UpsellCardComponent;

  public readonly slug: UpsellSlugs = UpsellSlugs.DeliverMedication;
  public readonly options: SelectableOption[] = [
    new SelectableOption('Yes, overnight my medicine', this.slug),
    new SelectableOption('No, I will pickup from my Pharmacy', QuestionnaireAnswers.No),
  ];
  public addressForm: FormGroup | null = null;

  private formService = inject(FormService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private consultationStorageService = inject(ConsultationStorageService);
  private readonly upsellToExclude = [UpsellSlugs.PriorityProcessing];

  public get hints(): string[] {
    return [
      `This option requires an additional $${
        environment.consultationRequestUpsells[this.slug]?.price
      } shipping and handling charge.`,
      'Overnight shipping is available Monday - Friday 12AM - 4PM CT. If your order was placed over the weekend, your medication will ship first thing Monday.',
    ];
  }

  /**
   * Retrieves additional forms to validate if the address form is enabled.
   */
  public get additionalFormsToValidate(): FormGroup[] {
    return this.addressForm ? [this.addressForm] : [];
  }

  /**
   * Sets up the address form based on the upsell option selected after the view has been initialized.
   */
  public ngAfterViewInit(): void {
    this.setAddressForm(this.upsellCard.control?.value);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Stores the shipping address in local storage and exclude upsells that should not co-exists if delivery is selected.
   *
   * @param {string} optionSelected the selected upsell option
   */
  public onContinueButtonPressed(optionSelected: string): void {
    this.setUpsellAddress(optionSelected);
    this.excludeUpsellsFromStorage(optionSelected);
  }

  /**
   * Configures the address form based on the current upsell option value.
   */
  public onOptionSelected(optionSelected: string) {
    optionSelected == this.slug ? this.setAddressForm(optionSelected) : (this.addressForm = null);
  }

  /**
   * Configures the address form based on the current upsell option value.
   *
   * @param {string} upsellControlValue the selected upsell control value
   */
  private setAddressForm(upsellControlValue: string): void {
    this.addressForm = upsellControlValue === this.slug ? this.formService.address : null;
    this.addressForm?.addControl('streetAddress2', new FormControl(null));
    this.patchAddressForm();
  }

  /**
   * Patches the address form with the shipping address stored in local storage.
   */
  private patchAddressForm(): void {
    const address = this.consultationStorageService.shippingAddress || this.consultationStorageService.address;

    if (!this.addressForm || !address) {
      return;
    }

    this.addressForm.patchValue(address);
  }

  /**
   * Set the upsell address when adding medication pickup delivery
   *
   * @param {string} optionSelected the selected upsell option
   */
  private setUpsellAddress(optionSelected: string): void {
    this.consultationStorageService.setUpsellAddress(
      this.slug,
      optionSelected === QuestionnaireAnswers.No ? null : this.addressForm.value,
    );
  }

  /**
   * Exclude upsells that should not co-exist in consultation request when medication pickup delivery upsell is added
   *
   * @param {string} optionSelected the selected upsell option
   */
  private excludeUpsellsFromStorage(optionSelected: string): void {
    if (optionSelected === QuestionnaireAnswers.No) {
      return;
    }

    this.consultationStorageService.upsells = this.consultationStorageService.upsells.filter(
      (upsell: UpsellSlugs) => !this.upsellToExclude.includes(upsell),
    );
  }
}
